import React from 'react';
import { useStyletron } from 'baseui';
import { Checkbox } from 'baseui/checkbox';
import { Input } from 'baseui/input';
import DeviceConfigBox from './DeviceConfigBox';
import FreeAppConfigBox from './FreeAppConfigBox';
import { getAdTypeStyle, getDeviceConfig, createLocalDeviceConfig, getNestedValue } from './utils';

interface AdTypeConfigProps {
	adType: string;
	adConfig: any;
	configPath: string[];
	editedConfig: any;
	globalConfig: any;
	onUpdate: (path: string[], value: any) => void;
}

const AdTypeConfig: React.FC<AdTypeConfigProps> = ({ adType, adConfig, configPath, editedConfig, globalConfig, onUpdate }) => {
	const [css] = useStyletron();

	const currentAdConfig = getNestedValue(editedConfig, [...configPath, adType]) || adConfig;
	const isEnabled = currentAdConfig?.enabled || false;
	const adStyle = getAdTypeStyle(adType, isEnabled);

	// Helper function to render device config box
	const renderDeviceConfigBox = (title: string, platform: string, subPlatform?: string) => {
		const deviceConfig = getDeviceConfig(currentAdConfig, platform, subPlatform, globalConfig);
		const isUsingGlobal = !currentAdConfig.deviceConfig;

		return (
			<DeviceConfigBox
				title={title}
				platform={platform}
				adConfig={currentAdConfig}
				configPath={configPath}
				adType={adType}
				subPlatform={subPlatform}
				deviceConfig={deviceConfig}
				isUsingGlobal={isUsingGlobal}
				onUpdate={onUpdate}
				createLocalDeviceConfig={(adCfg, plat, subPlat, updates) =>
					createLocalDeviceConfig(adCfg, plat, subPlat, updates, globalConfig)
				}
			/>
		);
	};

	return (
		<div
			key={adType}
			className={css({
				marginBottom: '32px',
				backgroundColor: '#ffffff',
				border: `2px solid ${adStyle.borderColor}`,
				borderRadius: '16px',
				overflow: 'hidden',
				boxShadow: `0 4px 12px ${adStyle.shadowColor}`,
				transition: 'all 0.3s ease'
			})}>
			{/* Header Section */}
			<div
				className={css({
					backgroundColor: isEnabled ? adStyle.bg : '#f8f9fa',
					padding: '20px',
					borderBottom: `1px solid ${adStyle.borderColor}30`
				})}>
				<div
					className={css({
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						flexWrap: 'wrap',
						gap: '16px'
					})}>
					{/* Ad Type Info */}
					<div
						className={css({
							display: 'flex',
							alignItems: 'center',
							gap: '16px'
						})}>
						<div>
							<h4
								className={css({
									fontSize: '1.4rem',
									fontWeight: '700',
									color: isEnabled ? adStyle.color : '#7f8c8d',
									margin: '0',
									marginBottom: '4px'
								})}>
								{adStyle.name}
							</h4>
							<div
								className={css({
									fontSize: '0.85rem',
									color: '#6c757d',
									fontWeight: '500'
								})}>
								{adType}
							</div>
						</div>
					</div>

					{/* Status Toggle */}
					<div
						className={css({
							display: 'flex',
							alignItems: 'center',
							gap: '12px',
							padding: '12px 20px',
							backgroundColor: '#ffffff',
							borderRadius: '30px',
							border: `2px solid ${adStyle.borderColor}`,
							boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
						})}>
						<Checkbox
							checked={isEnabled}
							onChange={(e) => onUpdate([...configPath, adType, 'enabled'], e.target.checked)}
							overrides={{
								Checkmark: {
									style: {
										borderColor: adStyle.color
									}
								}
							}}
						/>
						<span
							className={css({
								fontSize: '1rem',
								fontWeight: '700',
								color: isEnabled ? adStyle.color : '#6c757d',
								textTransform: 'uppercase',
								letterSpacing: '0.5px'
							})}>
							{isEnabled ? 'ENABLED' : 'DISABLED'}
						</span>
					</div>
				</div>

				{/* Placement ID */}
				{currentAdConfig?.placementId && (
					<div
						className={css({
							marginTop: '16px',
							padding: '12px 16px',
							backgroundColor: '#ffffff',
							borderRadius: '8px',
							border: '1px solid #dee2e6'
						})}>
						<div
							className={css({
								fontSize: '0.8rem',
								fontWeight: '600',
								color: '#6c757d',
								marginBottom: '4px',
								textTransform: 'uppercase',
								letterSpacing: '0.5px'
							})}>
							Placement ID
						</div>
						<div
							className={css({
								fontSize: '0.9rem',
								fontFamily: 'monospace',
								color: '#2c3e50',
								wordBreak: 'break-all'
							})}>
							{currentAdConfig.placementId}
						</div>
					</div>
				)}
			</div>

			{/* Configuration Section */}
			{isEnabled && (
				<div className={css({ padding: '24px' })}>
					{/* INTERSTITIAL specific controls */}
					{adType === 'INTERSTITIAL' && (
						<div
							className={css({
								display: 'grid',
								gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
								gap: '20px',
								marginBottom: '24px'
							})}>
							<div
								className={css({
									backgroundColor: '#f8f9fa',
									padding: '20px',
									borderRadius: '12px',
									border: '1px solid #dee2e6'
								})}>
								<label
									className={css({
										fontSize: '1rem',
										fontWeight: '600',
										color: '#495057',
										marginBottom: '12px',
										display: 'block'
									})}>
									Maximum Count
								</label>
								<Input
									value={currentAdConfig?.maxCount ?? ''}
									onChange={(e) => {
										const value = e.target.value.trim();
										onUpdate(
											[...configPath, adType, 'maxCount'],
											value === '' ? null : parseInt(value, 10) || null
										);
									}}
									placeholder='Enter maximum count'
									overrides={{
										Root: { style: { width: '100%' } },
										Input: {
											style: {
												fontSize: '1rem',
												padding: '14px',
												borderRadius: '8px'
											}
										}
									}}
								/>
							</div>

							<div
								className={css({
									backgroundColor: '#f8f9fa',
									padding: '20px',
									borderRadius: '12px',
									border: '1px solid #dee2e6'
								})}>
								<label
									className={css({
										fontSize: '1rem',
										fontWeight: '600',
										color: '#495057',
										marginBottom: '12px',
										display: 'block'
									})}>
									Frequency (Repeat After)
								</label>
								<Input
									value={currentAdConfig?.repeatAfter ?? ''}
									onChange={(e) => {
										const value = e.target.value.trim();
										onUpdate(
											[...configPath, adType, 'repeatAfter'],
											value === '' ? null : parseInt(value, 10) || null
										);
									}}
									placeholder='Enter frequency value'
									overrides={{
										Root: { style: { width: '100%' } },
										Input: {
											style: {
												fontSize: '1rem',
												padding: '14px',
												borderRadius: '8px'
											}
										}
									}}
								/>
							</div>
						</div>
					)}

					{/* Device Configuration - New Layout */}
					<div
						className={css({
							backgroundColor: '#f8f9fa',
							padding: '24px',
							borderRadius: '12px',
							border: '1px solid #e9ecef'
						})}>
						<div
							className={css({
								fontSize: '1.2rem',
								fontWeight: '700',
								color: '#2c3e50',
								marginBottom: '20px',
								display: 'flex',
								alignItems: 'center',
								gap: '8px'
							})}>
							<span
								className={css({
									width: '4px',
									height: '20px',
									backgroundColor: '#3498db',
									borderRadius: '2px'
								})}
							/>
							Device Specific Configuration
						</div>

						{/* Three main boxes: Android, iOS, com.zupee.free */}
						<div
							className={css({
								display: 'grid',
								gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
								gap: '16px',
								'@media (min-width: 1200px)': {
									gridTemplateColumns: '1fr 1fr 1fr'
								}
							})}>
							{/* Android Box */}
							{renderDeviceConfigBox('Android', 'android')}

							{/* iOS Box */}
							{renderDeviceConfigBox('iOS', 'ios')}

							{/* com.zupee.free Box */}
							<FreeAppConfigBox
								currentAdConfig={currentAdConfig}
								configPath={configPath}
								adType={adType}
								onUpdate={onUpdate}
								getDeviceConfig={(adCfg, plat, subPlat) => getDeviceConfig(adCfg, plat, subPlat, globalConfig)}
								createLocalDeviceConfig={(adCfg, plat, subPlat, updates) =>
									createLocalDeviceConfig(adCfg, plat, subPlat, updates, globalConfig)
								}
							/>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default AdTypeConfig;
